/**
 * SendScheduleModal
 * Modal for sending weekly schedules to subcontractors from the Planning page.
 * Shows list of TGs with assignment counts and generates mailto links.
 */

import { useState, useMemo } from 'react'
import { X, Mail, Calendar, Check, Loader2, ExternalLink, Download, AlertTriangle, ChevronDown, ChevronRight, Clock, RotateCcw } from 'lucide-react'
import { format, getISOWeek } from 'date-fns'
import { sv } from 'date-fns/locale'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import toast from 'react-hot-toast'

interface Assignment {
  id: string
  assignment_number: number | null
  title: string
  start_date: string
  start_time: string | null
  end_time: string | null
  location: string | null
  description: string | null
  status?: string
  customers?: {
    full_name?: string | null
  } | null
  assignment_subcontractors?: Array<{
    subcontractor_id: string
    schedule_sent_at?: string | null
    subcontractors?: {
      id: string
      name: string
      email?: string | null
      phone?: string | null
    } | null
  }>
}

interface Subcontractor {
  id: string
  name: string
  email?: string | null
  phone?: string | null
  company_name?: string | null
}

interface TgScheduleInfo {
  id: string
  name: string
  email?: string | null
  assignmentCount: number
  assignments: Assignment[]
  allSentThisWeek: boolean
}

interface SendScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  weekStart: Date
  weekEnd: Date
  assignments: Assignment[]
  subcontractors: Subcontractor[]
  onScheduleSent: () => void
}

export function SendScheduleModal({
  isOpen,
  onClose,
  weekStart,
  weekEnd,
  assignments,
  subcontractors,
  onScheduleSent
}: SendScheduleModalProps) {
  const { profile } = useAuth()
  const [sendingTo, setSendingTo] = useState<string | null>(null)
  const [sentTgs, setSentTgs] = useState<Set<string>>(new Set())
  const [expandedTgs, setExpandedTgs] = useState<Set<string>>(new Set())
  const [clearingTg, setClearingTg] = useState<string | null>(null)

  const weekNumber = getISOWeek(weekStart)
  const weekDateRange = `${format(weekStart, 'yyyy-MM-dd')} - ${format(weekEnd, 'yyyy-MM-dd')}`

  // Build TG schedule info
  const tgScheduleList = useMemo((): TgScheduleInfo[] => {
    const tgMap = new Map<string, TgScheduleInfo>()

    // Initialize with all subcontractors
    subcontractors.forEach(sub => {
      tgMap.set(sub.id, {
        id: sub.id,
        name: sub.name,
        email: sub.email,
        assignmentCount: 0,
        assignments: [],
        allSentThisWeek: true
      })
    })

    // Count assignments per TG
    assignments.forEach(assignment => {
      assignment.assignment_subcontractors?.forEach(as => {
        const tgInfo = tgMap.get(as.subcontractor_id)
        if (tgInfo) {
          tgInfo.assignmentCount++
          tgInfo.assignments.push(assignment)
          // Check if this assignment's schedule was already sent to this TG
          if (!as.schedule_sent_at) {
            tgInfo.allSentThisWeek = false
          }
        }
      })
    })

    // Filter to only TGs with assignments and sort by name
    return Array.from(tgMap.values())
      .filter(tg => tg.assignmentCount > 0)
      .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
  }, [assignments, subcontractors])

  // Count preliminary assignments
  const preliminaryAssignments = useMemo(() => {
    return assignments.filter(a => a.status === 'preliminar')
  }, [assignments])

  const hasPreliminaryWarning = preliminaryAssignments.length > 0

  const toggleExpanded = (tgId: string) => {
    setExpandedTgs(prev => {
      const next = new Set(prev)
      if (next.has(tgId)) {
        next.delete(tgId)
      } else {
        next.add(tgId)
      }
      return next
    })
  }

  // Generate mailto link for a TG
  const generateMailtoLink = (tg: TgScheduleInfo): string => {
    if (!tg.email) return ''

    const subject = `Schema för ${tg.name} - Vecka ${weekNumber}`

    let body = `Hej ${tg.name}!\n\n`
    body += `Här kommer ditt schema för vecka ${weekNumber} (${weekDateRange}):\n\n`

    // Sort assignments by date and time
    const sortedAssignments = [...tg.assignments].sort((a, b) => {
      const dateCompare = (a.start_date || '').localeCompare(b.start_date || '')
      if (dateCompare !== 0) return dateCompare
      return (a.start_time || '').localeCompare(b.start_time || '')
    })

    sortedAssignments.forEach(a => {
      body += '────────────────────────────────────────\n'

      const weekday = a.start_date
        ? format(new Date(a.start_date), 'EEEE', { locale: sv })
        : ''
      const weekdayCapitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1)
      const dateStr = a.start_date
        ? `${weekdayCapitalized} ${a.start_date}`
        : 'Datum saknas'
      const timeStr = a.start_time
        ? `${a.start_time.substring(0, 5)}${a.end_time ? ` - ${a.end_time.substring(0, 5)}` : ''}`
        : ''

      body += `📅 ${dateStr}${timeStr ? ` kl ${timeStr}` : ''}\n`
      body += `📋 Uppdrag: #${a.assignment_number || '?'} - ${a.title || 'Namnlöst uppdrag'}\n`

      // Use assignment location for address
      if (a.location) {
        const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.location)}`
        body += `📍 Adress: ${a.location}\n`
        body += `   Karta: ${mapsUrl}\n`
      }

      if (a.description) {
        // Truncate long descriptions
        const desc = a.description.length > 200
          ? a.description.substring(0, 200) + '...'
          : a.description
        body += `\n📝 Beskrivning: ${desc}\n`
      }

      // Add reporting link
      const reportUrl = `${window.location.origin}/subcontractor/assignments/${a.id}`
      body += `\n🔗 Se uppdraget i appen: ${reportUrl}\n`

      // Add colleagues with phone numbers
      const colleaguesList = a.assignment_subcontractors
        ?.filter(as => as.subcontractor_id !== tg.id && as.subcontractors)
        .map(as => ({
          name: as.subcontractors?.name || '',
          phone: as.subcontractors?.phone
        }))
        .filter(c => c.name)

      if (colleaguesList && colleaguesList.length > 0) {
        body += '\n🌿 Kollegor:\n'
        colleaguesList.forEach(c => {
          if (c.phone) {
            body += `   • ${c.name} (${c.phone})\n`
          } else {
            body += `   • ${c.name}\n`
          }
        })
      }
    })

    body += '────────────────────────────────────────\n\n'
    body += 'Med vänliga hälsningar,\n'
    body += `${profile?.full_name || 'Admin'}\n`
    body += 'MB Trädgård Support AB'

    return `mailto:${tg.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  // Generate ICS content for a TG
  const generateICSContent = (tg: TgScheduleInfo): string => {
    let icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//MB Trädgård//Schema//SV
CALSCALE:GREGORIAN
METHOD:PUBLISH
X-WR-CALNAME:Schema vecka ${weekNumber}
`

    const sortedAssignments = [...tg.assignments].sort((a, b) => {
      const dateCompare = (a.start_date || '').localeCompare(b.start_date || '')
      if (dateCompare !== 0) return dateCompare
      return (a.start_time || '').localeCompare(b.start_time || '')
    })

    sortedAssignments.forEach(a => {
      if (!a.start_date) return

      const startDate = a.start_date.replace(/-/g, '')
      const startTime = a.start_time ? a.start_time.replace(/:/g, '').substring(0, 4) + '00' : '080000'
      const endTime = a.end_time ? a.end_time.replace(/:/g, '').substring(0, 4) + '00' : '170000'

      // Use assignment location for address
      const location = a.location || ''

      const reportUrl = `${window.location.origin}/subcontractor/assignments/${a.id}`
      const mapsUrl = location
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`
        : ''

      // Format date with weekday
      const weekday = a.start_date
        ? format(new Date(a.start_date), 'EEEE', { locale: sv })
        : ''
      const weekdayCapitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1)
      const timeStr = a.start_time
        ? `${a.start_time.substring(0, 5)}${a.end_time ? ` - ${a.end_time.substring(0, 5)}` : ''}`
        : ''

      // Build description
      let description = `📅 ${weekdayCapitalized} ${a.start_date}${timeStr ? ` kl ${timeStr}` : ''}`
      description += `\\n📋 Uppdrag: #${a.assignment_number || '?'} - ${a.title || 'Uppdrag'}`
      if (location) {
        description += `\\n\\n📍 Adress: ${location}`
        if (mapsUrl) description += `\\n🗺️ Karta: ${mapsUrl}`
      }
      if (a.description) {
        description += `\\n\\n📝 Beskrivning:\\n${a.description.replace(/\n/g, '\\n').substring(0, 300)}`
      }
      description += `\\n\\n🔗 Se uppdraget: ${reportUrl}`

      // Add colleagues with phone numbers
      const colleaguesList = a.assignment_subcontractors
        ?.filter(as => as.subcontractor_id !== tg.id && as.subcontractors)
        .map(as => {
          const name = as.subcontractors?.name || ''
          const phone = as.subcontractors?.phone
          return { name, phone }
        })
        .filter(c => c.name)

      if (colleaguesList && colleaguesList.length > 0) {
        description += '\\n\\n🌿 Kollegor:'
        colleaguesList.forEach(c => {
          if (c.phone) {
            description += `\\n   • ${c.name} (${c.phone})`
          } else {
            description += `\\n   • ${c.name}`
          }
        })
      }

      // Escape special characters for ICS
      const escapeICS = (text: string) => text.replace(/[,;\\]/g, '\\$&')

      // Use assignment location in title
      const summaryLocation = location || 'Okänd adress'

      icsContent += `BEGIN:VEVENT
DTSTART:${startDate}T${startTime}
DTEND:${startDate}T${endTime}
SUMMARY:${escapeICS(`#${a.assignment_number || '?'} ${a.title || 'Uppdrag'} - ${summaryLocation}`)}
DESCRIPTION:${description}
LOCATION:${escapeICS(location)}
UID:${a.id}-${tg.id}@tsa
END:VEVENT
`
    })

    icsContent += 'END:VCALENDAR'
    return icsContent
  }

  // Download ICS file
  const downloadICS = (tg: TgScheduleInfo) => {
    const icsContent = generateICSContent(tg)
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `schema-${tg.name.toLowerCase().replace(/\s+/g, '-')}-vecka-${weekNumber}.ics`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Mark schedule as sent for a TG
  const markAsSent = async (tg: TgScheduleInfo) => {
    const now = new Date().toISOString()

    for (const assignment of tg.assignments) {
      const { error } = await supabase
        .from('assignment_subcontractors')
        .update({ schedule_sent_at: now })
        .eq('assignment_id', assignment.id)
        .eq('subcontractor_id', tg.id)

      if (error) {
        console.error('Error marking schedule as sent:', error)
        throw error
      }
    }
  }

  // Clear sent status for a TG (allows resending)
  const clearSentStatus = async (tg: TgScheduleInfo) => {
    setClearingTg(tg.id)
    try {
      for (const assignment of tg.assignments) {
        const { error } = await supabase
          .from('assignment_subcontractors')
          .update({ schedule_sent_at: null })
          .eq('assignment_id', assignment.id)
          .eq('subcontractor_id', tg.id)

        if (error) {
          console.error('Error clearing sent status:', error)
          throw error
        }
      }

      // Remove from local sent state
      setSentTgs(prev => {
        const next = new Set(prev)
        next.delete(tg.id)
        return next
      })

      toast.success(`Återställde skickad-status för ${tg.name}`)
      onScheduleSent() // Refresh data
    } catch (error) {
      console.error('Error clearing sent status:', error)
      toast.error('Kunde inte återställa status')
    } finally {
      setClearingTg(null)
    }
  }

  // Send schedule to a TG (open mailto + mark as sent)
  const sendToTg = async (tg: TgScheduleInfo) => {
    setSendingTo(tg.id)
    try {
      // Open mailto link
      const mailtoLink = generateMailtoLink(tg)
      if (mailtoLink) {
        window.open(mailtoLink, '_blank')
      }

      // Download ICS file
      downloadICS(tg)

      // Mark as sent
      await markAsSent(tg)

      setSentTgs(prev => new Set([...prev, tg.id]))
      toast.success(`Schema skickat till ${tg.name}`)
    } catch (error) {
      console.error('Error sending schedule:', error)
      toast.error('Kunde inte markera schema som skickat')
    } finally {
      setSendingTo(null)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                  Skicka veckoschema
                </h3>
                <p className="text-sm text-nature-500 dark:text-nature-400">
                  Vecka {weekNumber} ({weekDateRange})
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {tgScheduleList.length === 0 ? (
            <div className="text-center py-8 text-nature-500 dark:text-nature-400">
              Inga trädgårdsmästare har uppdrag denna vecka
            </div>
          ) : (
            <>
              {/* Warning for preliminary assignments */}
              {hasPreliminaryWarning && (
                <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-200">
                        Det finns {preliminaryAssignments.length} preliminär{preliminaryAssignments.length === 1 ? 't' : 'a'} uppdrag denna vecka
                      </p>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                        Dessa uppdrag är inte bekräftade och kan komma att ändras. Överväg att bekräfta dem innan du skickar schemat.
                      </p>
                      <div className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                        {preliminaryAssignments.slice(0, 5).map(a => (
                          <div key={a.id} className="flex items-center gap-1">
                            <span>• #{a.assignment_number} {a.title}</span>
                            {a.start_date && <span className="text-amber-500">({a.start_date})</span>}
                          </div>
                        ))}
                        {preliminaryAssignments.length > 5 && (
                          <div className="italic mt-1">...och {preliminaryAssignments.length - 5} till</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TG List */}
              <div className="space-y-2">
                {tgScheduleList.map(tg => {
                  const isSending = sendingTo === tg.id
                  const wasSent = sentTgs.has(tg.id)
                  const hasEmail = !!tg.email
                  const isExpanded = expandedTgs.has(tg.id)

                  // Sort assignments by date and time
                  const sortedAssignments = [...tg.assignments].sort((a, b) => {
                    const dateCompare = (a.start_date || '').localeCompare(b.start_date || '')
                    if (dateCompare !== 0) return dateCompare
                    return (a.start_time || '').localeCompare(b.start_time || '')
                  })

                  // Check if any of this TG's assignments are preliminary
                  const hasPreliminary = tg.assignments.some(a => a.status === 'preliminar')

                  return (
                    <div
                      key={tg.id}
                      className={`rounded-lg border transition-colors border-nature-200 dark:border-nature-700 bg-white dark:bg-nature-700/50 ${!hasEmail ? 'opacity-60' : ''}`}
                    >
                      <div className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-nature-900 dark:text-nature-100">
                                  {tg.name}
                                </span>
                                {(wasSent || tg.allSentThisWeek) && (
                                  <>
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                      <Check className="h-3 w-3" />
                                      Skickat
                                    </span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        clearSentStatus(tg)
                                      }}
                                      disabled={clearingTg === tg.id}
                                      className="p-1 text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-600 rounded transition-colors"
                                      title="Återställ så du kan skicka igen"
                                    >
                                      {clearingTg === tg.id ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      ) : (
                                        <RotateCcw className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                  </>
                                )}
                                {hasPreliminary && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                    <AlertTriangle className="h-3 w-3" />
                                    Preliminär
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => toggleExpanded(tg.id)}
                                className="flex items-center gap-1 text-sm text-nature-500 dark:text-nature-400 hover:text-nature-700 dark:hover:text-nature-200"
                              >
                                {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                <span>{tg.assignmentCount} uppdrag</span>
                                {!hasEmail && <span> • Saknar e-post</span>}
                              </button>
                          </div>

                          <div className="flex items-center gap-2">
                            {hasEmail && !wasSent && !tg.allSentThisWeek && (
                              <button
                                onClick={() => sendToTg(tg)}
                                disabled={isSending}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                              >
                                {isSending ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <ExternalLink className="h-4 w-4" />
                                )}
                                Skicka
                              </button>
                            )}
                            <button
                              onClick={() => downloadICS(tg)}
                              className="p-1.5 text-nature-500 hover:text-nature-700 dark:hover:text-nature-300"
                              title="Ladda ner kalenderfil"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Expandable assignment list */}
                      {isExpanded && (
                        <div className="border-t border-nature-200 dark:border-nature-600 px-3 py-2 bg-nature-50 dark:bg-nature-800/50">
                          <div className="space-y-1.5">
                            {sortedAssignments.map(a => {
                              const isPreliminary = a.status === 'preliminar'
                              const weekday = a.start_date
                                ? format(new Date(a.start_date), 'EEE', { locale: sv })
                                : ''

                              return (
                                <div
                                  key={a.id}
                                  className={`flex items-center gap-2 text-sm py-1 px-2 rounded ${
                                    isPreliminary
                                      ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                                      : 'bg-white dark:bg-nature-700'
                                  }`}
                                >
                                  <div className="flex items-center gap-1.5 text-nature-500 dark:text-nature-400 min-w-[80px]">
                                    <Calendar className="h-3.5 w-3.5" />
                                    <span className="capitalize">{weekday}</span>
                                    {a.start_time && (
                                      <>
                                        <Clock className="h-3 w-3 ml-1" />
                                        <span>{a.start_time.substring(0, 5)}</span>
                                      </>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="text-nature-600 dark:text-nature-300">
                                      #{a.assignment_number}
                                    </span>
                                    <span className="ml-1.5 text-nature-900 dark:text-nature-100 truncate">
                                      {a.title}
                                    </span>
                                    {a.location && (
                                      <span className="ml-1.5 text-nature-500 dark:text-nature-400 truncate">
                                        – {a.location}
                                      </span>
                                    )}
                                  </div>
                                  {isPreliminary && (
                                    <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                                      Preliminär
                                    </span>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="text-sm text-nature-500 dark:text-nature-400 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Mejl + kalenderfil (.ics) öppnas för varje TG</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="btn-outline px-4 py-2"
              >
                Stäng
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
